import { useEffect, useRef } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useCreateWebhook } from '@/services/db/webhook/mutations';
import { useOptionalWebhooks } from '@/services/db/webhook/queries';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useReportingWebhookFormAnalytics } from '../analytics';
import { useReportingWebhooksIndexHref } from '../hooks';
import { ReportingWebhookForm } from './reporting-webhook-form';
import {
  describeWebhook,
  type ReportingWebhookFormInput,
  type ReportingWebhookFormValues,
} from './reporting-webhook-schema';
import type { ReportingWebhookFormApi } from './types';

/** Only the re-baseline is needed here; the form callback hands back the plain RHF instance. */
type FormResetter = Pick<ReportingWebhookFormApi, 'reset'>;

const CREATE_DEFAULTS: ReportingWebhookFormInput = {
  name: '',
  endpoint_url: '',
  events: [],
  resolution: false,
  with_content: false,
  enabled: false,
};

/**
 * Creates a reporting webhook. Owns the blank defaults, the create call and its outcomes; the form
 * owns the fields.
 */
type CreateReportingWebhookProps = {
  /** Resolved by the page. Never read from the URL here — this renders inside a Suspense boundary. */
  workspaceId: number;
};

export function CreateReportingWebhook({ workspaceId }: CreateReportingWebhookProps) {
  const { hasPermission } = usePermissions();
  const { webhooks, isLoaded } = useOptionalWebhooks({ workspaceId });
  const snackbars = useSnackbars();
  const navigate = useSafeNavigate();
  const indexHref = useReportingWebhooksIndexHref(String(workspaceId));
  const createWebhook = useCreateWebhook();
  const analytics = useReportingWebhookFormAnalytics();

  // Waits for the list rather than firing on mount, because `isFirstWebhook` is a claim about the
  // workspace and an empty in-flight list would report every workspace as empty. A list that never
  // arrives sends nothing at all — Ember fires this from inside the `try`, after the await, so a
  // failed fetch means no event rather than a wrong one.
  const hasTrackedOpen = useRef(false);
  useEffect(() => {
    if (!isLoaded || hasTrackedOpen.current) return;
    hasTrackedOpen.current = true;
    void analytics.trackCreateOpened({ isFirstWebhook: webhooks.length === 0 });
  }, [isLoaded, webhooks, analytics]);

  /**
   * The one save both entry points run. `shouldRedirect` is the only difference: the footer button
   * sends the user to the list afterwards, while the leave guard must not — it is already holding
   * the user's real destination, and `safeNavigate` keeps a single pending-transition subscription,
   * so a second navigation here would replace theirs and land them on the list instead.
   */
  const save = async (
    values: ReportingWebhookFormValues,
    form: FormResetter,
    { shouldRedirect }: { shouldRedirect: boolean },
  ) => {
    try {
      await createWebhook.mutateAsync(values);
      // Re-baseline before navigating: both leave-guards read `isDirty`, and the blocker is
      // evaluated when the router actually navigates — by which point `isSaving` has settled back
      // to false, so a still-dirty form would prompt about unsaved changes we just saved.
      form.reset(values, { keepDefaultValues: false });
      snackbars.create({
        variant: 'default',
        id: 'integrations.reporting-webhooks.new.success',
        message: `${describeWebhook(values.name)} added.`,
      });
      if (shouldRedirect) {
        navigate(indexHref);
      }
    } catch {
      snackbars.create({
        variant: 'critical',
        id: 'integrations.reporting-webhooks.new.error',
        message: "Your webhook couldn't be added. Check your connection and try again.",
      });
    }
  };

  const onSubmit = (values: ReportingWebhookFormValues, form: FormResetter) =>
    save(values, form, { shouldRedirect: true });

  const onSaveAndStay = (values: ReportingWebhookFormValues, form: FormResetter) =>
    save(values, form, { shouldRedirect: false });

  return (
    <ReportingWebhookForm
      onSubmit={onSubmit}
      onSaveAndStay={onSaveAndStay}
      defaultValues={CREATE_DEFAULTS}
      signingKey=""
      webhookId=""
      otherEndpointUrls={webhooks.map((webhook) => webhook.endpoint_url)}
      isLastEnabledWebhook={false}
      readOnly={hasPermission('journeys/reporting_webhooks/edit') !== true}
      isSaving={createWebhook.isPending}
      cancelHref={indexHref}
    />
  );
}
